import { useState, useMemo } from "react";
import { Trash2, Download, Phone, Mail, Clock, Inbox } from "lucide-react";
import { formatDate } from "./adminHelpers";

const PRODUCT_LABELS = {
  "diy-kit": "ערכת DIY",
  "body-soap": "סבון גוף",
};

const productLabel = (p) => PRODUCT_LABELS[p] || p || "ערכת DIY";

const ProductBadge = ({ product }) => {
  const isSoap = product === "body-soap";
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${
        isSoap ? "bg-[#C47F64]/12 text-[#C47F64]" : "bg-[#4A5D43]/12 text-[#4A5D43]"
      }`}
    >
      {productLabel(product)}
    </span>
  );
};

export const WaitlistPanel = ({ entries, onDelete }) => {
  const [productFilter, setProductFilter] = useState("all");

  const counts = useMemo(() => {
    const c = { all: entries.length, "diy-kit": 0, "body-soap": 0 };
    entries.forEach((e) => {
      const p = e.product || "diy-kit";
      c[p] = (c[p] || 0) + 1;
    });
    return c;
  }, [entries]);

  const filtered = useMemo(() => {
    if (productFilter === "all") return entries;
    return entries.filter((e) => (e.product || "diy-kit") === productFilter);
  }, [entries, productFilter]);

  const exportCSV = () => {
    const headers = ["מספר", "שם", "טלפון", "אימייל", "מוצר", "תאריך הרשמה"];
    const rows = filtered.map((e, i) => [
      i + 1, e.name || "ללא שם", e.phone || "-", e.email || "-",
      productLabel(e.product), formatDate(e.created_at),
    ]);
    const BOM = "\uFEFF";
    const csv = BOM + [headers.join(","), ...rows.map((r) => r.map((c) => `"${c}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `mart_waitlist_${productFilter}_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const filterTabs = [
    { key: "all", label: "הכל" },
    { key: "diy-kit", label: "ערכת DIY" },
    { key: "body-soap", label: "סבון גוף" },
  ];

  return (
    <div data-testid="admin-waitlist-panel">
      {/* Filter + export bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          {filterTabs.map((t) => (
            <button
              key={t.key}
              data-testid={`waitlist-filter-${t.key}`}
              onClick={() => setProductFilter(t.key)}
              className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-colors border ${
                productFilter === t.key
                  ? "bg-[#2C3329] text-white border-[#2C3329]"
                  : "bg-white dark:bg-[#1a231a] text-[#5C5C5C] dark:text-[#9A9790] border-[#2C3329]/10 hover:border-[#2C3329]/25"
              }`}
            >
              {t.label} ({counts[t.key] || 0})
            </button>
          ))}
        </div>
        <button
          data-testid="waitlist-export-csv"
          onClick={exportCSV}
          disabled={filtered.length === 0}
          className="flex items-center justify-center gap-2 px-4 py-2 rounded-sm text-xs font-medium bg-[#4A5D43] hover:bg-[#3A4A35] disabled:opacity-50 text-white transition-colors"
        >
          <Download className="h-3.5 w-3.5" />
          ייצוא CSV
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white dark:bg-[#1a231a] border border-[#2C3329]/10 dark:border-[#2a3a2a] rounded-sm py-16 text-center">
          <Inbox className="h-8 w-8 text-[#C4C4BC] mx-auto mb-3" />
          <p className="font-sans text-sm text-[#9A9790]">אין נרשמים ברשימה זו עדיין</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white dark:bg-[#1a231a] border border-[#2C3329]/10 dark:border-[#2a3a2a] rounded-sm overflow-hidden">
            <table className="w-full text-right">
              <thead className="bg-[#FAF8F5] dark:bg-[#0f1510] border-b border-[#2C3329]/10">
                <tr className="text-[11px] uppercase tracking-[0.05em] text-[#5C5C5C] dark:text-[#9A9790]">
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-4 py-3 font-medium">שם</th>
                  <th className="px-4 py-3 font-medium">טלפון</th>
                  <th className="px-4 py-3 font-medium">אימייל</th>
                  <th className="px-4 py-3 font-medium">מוצר</th>
                  <th className="px-4 py-3 font-medium">תאריך</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e, i) => (
                  <tr key={e.waitlist_id || i} className="border-b border-[#2C3329]/5 hover:bg-[#FAF8F5]/60 dark:hover:bg-[#0f1510]/40 text-sm text-[#2C3329] dark:text-white">
                    <td className="px-4 py-3 text-[#A3A3A3]">{i + 1}</td>
                    <td className="px-4 py-3 font-medium">{e.name || "ללא שם"}</td>
                    <td className="px-4 py-3" dir="ltr"><span className="block text-right">{e.phone || "-"}</span></td>
                    <td className="px-4 py-3" dir="ltr"><span className="block text-right">{e.email || "-"}</span></td>
                    <td className="px-4 py-3"><ProductBadge product={e.product} /></td>
                    <td className="px-4 py-3 text-[#5C5C5C] dark:text-[#9A9790] text-xs">{formatDate(e.created_at)}</td>
                    <td className="px-4 py-3">
                      <button
                        data-testid={`waitlist-delete-${e.waitlist_id}`}
                        onClick={() => onDelete(e.waitlist_id)}
                        className="p-1.5 rounded-sm text-[#C67D63] hover:bg-[#C67D63]/10 transition-colors"
                        aria-label="מחיקה"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {filtered.map((e, i) => (
              <div key={e.waitlist_id || i} className="bg-white dark:bg-[#1a231a] border border-[#2C3329]/10 dark:border-[#2a3a2a] rounded-sm p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="font-medium text-[#2C3329] dark:text-white text-sm">{e.name || "ללא שם"}</p>
                    <div className="mt-1"><ProductBadge product={e.product} /></div>
                  </div>
                  <button
                    data-testid={`waitlist-delete-${e.waitlist_id}`}
                    onClick={() => onDelete(e.waitlist_id)}
                    className="p-1.5 rounded-sm text-[#C67D63] hover:bg-[#C67D63]/10 transition-colors shrink-0"
                    aria-label="מחיקה"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="space-y-1.5 text-sm text-[#5C5C5C] dark:text-[#9A9790]">
                  {e.phone && (
                    <div className="flex items-center gap-2" dir="ltr">
                      <Phone className="h-3.5 w-3.5 text-[#7B8C96] shrink-0" />
                      <span>{e.phone}</span>
                    </div>
                  )}
                  {e.email && (
                    <div className="flex items-center gap-2" dir="ltr">
                      <Mail className="h-3.5 w-3.5 text-[#7B8C96] shrink-0" />
                      <span className="truncate">{e.email}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 text-[#7B8C96] shrink-0" />
                    <span className="text-xs">{formatDate(e.created_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default WaitlistPanel;
